import React, { type ReactNode } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

/**
 * Props passed by createRenderer — { element, children, emit }.
 * element.props contains the resolved component props.
 */
interface RenderProps<P = Record<string, unknown>> {
  element: { props: P };
  children?: ReactNode;
}

// Color maps
const BAR_COLORS: Record<string, string> = {
  default: "bg-default-600",
  green: "bg-green-500",
  red: "bg-red-500",
  yellow: "bg-yellow-500",
};

const STAT_COLORS: Record<string, string> = {
  default: "border-slate-200",
  green: "border-green-300 bg-green-50",
  red: "border-red-300 bg-red-50",
  yellow: "border-yellow-300 bg-yellow-50",
  blue: "border-blue-300 bg-blue-50",
};

const BADGE_COLORS: Record<string, string> = {
  default: "bg-slate-100 text-slate-700",
  green: "bg-green-100 text-green-700",
  red: "bg-red-100 text-red-700",
  yellow: "bg-yellow-100 text-yellow-800",
  blue: "bg-blue-100 text-blue-700",
  purple: "bg-purple-100 text-purple-700",
};

const CALLOUT_STYLES: Record<string, string> = {
  info: "border-blue-200 bg-blue-50 text-blue-900",
  warning: "border-yellow-200 bg-yellow-50 text-yellow-900",
  success: "border-green-200 bg-green-50 text-green-900",
  error: "border-red-200 bg-red-50 text-red-900",
};

const CHECK_ICONS: Record<string, { icon: string; color: string }> = {
  green: { icon: "\u2713", color: "text-green-500" },
  red: { icon: "\u2717", color: "text-red-500" },
  default: { icon: "\u2022", color: "text-slate-400" },
};

// --- Layout ---

export function Stack({ element, children }: RenderProps) {
  return (
    <div className="flex flex-col gap-6">
      {children}
    </div>
  );
}

export function Grid({ element, children }: RenderProps<{ cols?: number; gap?: number }>) {
  const { cols = 2, gap = 4 } = element.props ?? {};
  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: `${gap * 0.25}rem`,
      }}
    >
      {children}
    </div>
  );
}

export function Section({ element, children }: RenderProps<{ title: string }>) {
  const { title } = element.props ?? {} as any;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

export function Card({ element, children }: RenderProps<{ title?: string }>) {
  const { title } = element.props ?? {} as any;
  return (
    <div className="rounded-lg shadow-md ring-1 ring-black/5 border border-slate-200 bg-white">
      {title && (
        <div className="px-6 py-3 border-b border-slate-200">
          <h3 className="text-xl font-semibold">{title}</h3>
        </div>
      )}
      <div className="py-4 px-6 space-y-6">{children}</div>
    </div>
  );
}

export function Divider() {
  return <hr className="border-slate-200" />;
}

// --- Data display ---

export function ScoreBar({ element }: RenderProps<{
  label: string;
  value: number;
  max?: number;
  color?: string;
}>) {
  const { label, value, max = 100, color = "default" } = element.props ?? {} as any;
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const barColor = BAR_COLORS[color] ?? BAR_COLORS.default;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <span className="text-sm font-semibold text-slate-900 tabular-nums">
          {value}/{max}
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function StatCard({ element }: RenderProps<{
  label: string;
  value: string | number;
  unit?: string;
  color?: string;
}>) {
  const { label, value, unit, color = "default" } = element.props ?? {} as any;
  const colorClass = STAT_COLORS[color] ?? STAT_COLORS.default;

  return (
    <div className={`border rounded-lg px-4 py-3 ${colorClass}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums">
        {value}
        {unit && <span className="text-sm font-normal text-slate-500 ml-1">{unit}</span>}
      </div>
    </div>
  );
}

export function KeyValue({ element }: RenderProps<{
  label: string;
  value: string | number | boolean;
}>) {
  const { label, value } = element.props ?? {} as any;
  const display = typeof value === "boolean"
    ? (value ? "Oui" : "Non")
    : String(value ?? "");

  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{display}</span>
    </div>
  );
}

export function Table({ element }: RenderProps<{
  headers: string[];
  rows: string[][];
}>) {
  const { headers = [], rows = [] } = element.props ?? {} as any;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50">
            {headers.map((h: string, i: number) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-slate-600 border-b border-slate-200">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row: string[], ri: number) => (
            <tr key={ri} className="border-b border-slate-100 last:border-0">
              {row.map((cell: string, ci: number) => (
                <td key={ci} className="px-3 py-2 text-slate-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Lists ---

export function BulletList({ element }: RenderProps<{ items: string[] }>) {
  const { items = [] } = element.props ?? {} as any;
  return (
    <ul className="space-y-1.5">
      {items.map((item: string, i: number) => (
        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
          <span className="text-slate-400 mt-0.5">{"\u2022"}</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function CheckList({ element }: RenderProps<{
  items: string[];
  color?: string;
}>) {
  const { items = [], color = "default" } = element.props ?? {} as any;
  const style = CHECK_ICONS[color] ?? CHECK_ICONS.default;

  return (
    <ul className="space-y-1.5">
      {items.map((item: string, i: number) => (
        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
          <span className={`mt-0.5 font-bold ${style.color}`}>{style.icon}</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function NumberedList({ element }: RenderProps<{ items: string[] }>) {
  const { items = [] } = element.props ?? {} as any;
  return (
    <ol className="space-y-1.5">
      {items.map((item: string, i: number) => (
        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
          <span className="text-slate-400 font-medium tabular-nums min-w-[1.25rem]">{i + 1}.</span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

// --- Content ---

export function Text({ element }: RenderProps<{
  content: string;
  variant?: string;
}>) {
  const { content, variant = "default" } = element.props ?? {} as any;
  const variantClass = variant === "muted"
    ? "text-slate-500"
    : variant === "bold"
      ? "font-semibold text-slate-900"
      : "text-slate-700";

  return <p className={`text-sm leading-relaxed ${variantClass}`}>{content}</p>;
}

export function Callout({ element }: RenderProps<{
  title?: string;
  content: string;
  variant?: string;
}>) {
  const { title, content, variant = "info" } = element.props ?? {} as any;
  const style = CALLOUT_STYLES[variant] ?? CALLOUT_STYLES.info;

  return (
    <div className={`border rounded-lg px-4 py-3 ${style}`}>
      {title && (
        <div className="font-semibold text-sm mb-1">{title}</div>
      )}
      <div className="text-sm leading-relaxed">{content}</div>
    </div>
  );
}

export function BadgeComponent({ element }: RenderProps<{
  label: string;
  color?: string;
}>) {
  const { label, color = "default" } = element.props ?? {} as any;
  const colorClass = BADGE_COLORS[color] ?? BADGE_COLORS.default;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
}

export function Heading({ element }: RenderProps<{
  text: string;
  level?: string;
}>) {
  const { text, level = "h2" } = element.props ?? {} as any;

  if (level === "h1") return <h1 className="text-xl font-semibold text-slate-900">{text}</h1>;
  if (level === "h3") return <h3 className="text-sm font-semibold text-slate-700">{text}</h3>;
  return <h2 className="text-base font-semibold text-slate-800">{text}</h2>;
}

// --- Interactive ---

export function DialogComponent({ element, children }: RenderProps<{
  ctaLabel: string;
  title?: string;
}>) {
  const { ctaLabel, title } = element.props ?? {} as any;
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-default-600 bg-default-50 hover:bg-default-100 border border-default-200 rounded-lg transition-colors"
      >
        {ctaLabel}
      </button>
      {open && (
        <div className="fixed inset-0 z-[9999]">
          <div className="fixed inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
            <div
              className="w-full max-w-2xl max-h-[80vh] rounded-xl bg-white shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {title && (
                <>
                  <div className="px-6 py-3 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="text-slate-400 hover:text-slate-500"
                    >
                      <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="border-b border-slate-200" />
                </>
              )}
              <div className="px-6 py-4 overflow-y-auto space-y-6">
                {children}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// --- Charts ---

const CHART_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#3b82f6", "#a855f7", "#ec4899", "#14b8a6"];

export function BarChartComponent({ element }: RenderProps<{
  title?: string;
  data: Array<{ label: string; value: number }>;
  color?: string;
  layout?: string;
}>) {
  const { title, data = [], color, layout: barLayout = "vertical" } = element.props ?? {} as any;
  const barColor = color || CHART_COLORS[0];
  const isHorizontal = barLayout === "horizontal";

  return (
    <div>
      {title && <div className="text-sm font-medium text-slate-700 mb-2">{title}</div>}
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * (isHorizontal ? 36 : 0) + 60)}>
        <BarChart data={data} layout={isHorizontal ? "vertical" : "horizontal"} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          {/* Axes MUST be direct children — recharts discovers XAxis/YAxis by
              traversing direct children by type and does not recurse into
              fragments. Switch props by layout, never swap the elements. */}
          <XAxis
            type={isHorizontal ? "number" : "category"}
            dataKey={isHorizontal ? undefined : "label"}
            tick={{ fontSize: 12, fill: "#64748b" }}
          />
          <YAxis
            type={isHorizontal ? "category" : "number"}
            dataKey={isHorizontal ? "label" : undefined}
            width={isHorizontal ? 120 : 48}
            tick={{ fontSize: 12, fill: "#64748b" }}
          />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          <Bar dataKey="value" fill={barColor} radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function LineChartComponent({ element }: RenderProps<{
  title?: string;
  data: Array<Record<string, unknown>>;
  lines: Array<{ key: string; color?: string; label?: string }>;
  xKey?: string;
}>) {
  const { title, data = [], lines = [], xKey = "label" } = element.props ?? {} as any;

  return (
    <div>
      {title && <div className="text-sm font-medium text-slate-700 mb-2">{title}</div>}
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: "#64748b" }} />
          <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {lines.map((line: any, i: number) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              stroke={line.color || CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              name={line.label || line.key}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PieChartComponent({ element }: RenderProps<{
  title?: string;
  data: Array<{ label: string; value: number }>;
  donut?: boolean;
}>) {
  const { title, data = [], donut = false } = element.props ?? {} as any;

  return (
    <div>
      {title && <div className="text-sm font-medium text-slate-700 mb-2">{title}</div>}
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={donut ? 50 : 0}
            outerRadius={90}
            paddingAngle={2}
            isAnimationActive={false}
          >
            {data.map((_: any, i: number) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
